import React from "react";
import { Alert, Space, Typography } from "antd";
import {
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { extractErrorInfo } from "@/utils/api";

const { Text } = Typography;

interface ErrorNotificationProps {
  error: any;
  onClose?: () => void;
  showDetails?: boolean;
  className?: string;
}

export const ErrorNotification: React.FC<ErrorNotificationProps> = ({
  error,
  onClose,
  showDetails = false,
  className,
}) => {
  const errorInfo = extractErrorInfo(error);
  const { message, code, error: errorType, details, retryAfter } = errorInfo;

  // Determine alert type based on error type
  const getAlertType = () => {
    switch (errorType) {
      case "ValidationError":
        return "warning";
      case "Unauthorized":
      case "Forbidden":
        return "error";
      case "TooManyRequests":
        return "warning";
      case "InternalServerError":
        return "error";
      default:
        return "error";
    }
  };

  // Get appropriate icon
  const getIcon = () => {
    switch (errorType) {
      case "ValidationError":
        return <ExclamationCircleOutlined />;
      case "Unauthorized":
      case "Forbidden":
        return <CloseCircleOutlined />;
      case "TooManyRequests":
        return <ExclamationCircleOutlined />;
      case "InternalServerError":
        return <CloseCircleOutlined />;
      default:
        return <InfoCircleOutlined />;
    }
  };

  // Get user-friendly title
  const getTitle = () => {
    switch (errorType) {
      case "ValidationError":
        return "Validation Error";
      case "Unauthorized":
        return "Authentication Required";
      case "Forbidden":
        return "Access Denied";
      case "TooManyRequests":
        return "Rate Limit Exceeded";
      case "InternalServerError":
        return "Server Error";
      case "NotFound":
        return "Not Found";
      default:
        return "Error";
    }
  };

  // Get actionable message
  const getActionableMessage = () => {
    if (retryAfter) {
      return `Please wait ${retryAfter} seconds before trying again.`;
    }

    switch (code) {
      case "INVALID_CREDENTIALS":
        return "Please check your username and password and try again.";
      case "NO_ACTIVE_SESSION":
        return "Please log in again to continue.";
      case "RATE_LIMIT_EXCEEDED":
        return "Please wait a moment before trying again.";
      case "VALIDATION_ERROR":
        return "Please check your input and try again.";
      default:
        return "Please try again or contact support if the problem persists.";
    }
  };

  return (
    <Alert
      message={
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <div>
            <Text strong>{getTitle()}</Text>
            {code && (
              <Text
                type="secondary"
                style={{ marginLeft: 8, fontSize: "12px" }}
              >
                ({code})
              </Text>
            )}
          </div>
          <Text>{message}</Text>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {getActionableMessage()}
          </Text>
        </Space>
      }
      description={
        showDetails &&
        details && (
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Technical Details:
            </Text>
            <pre style={{ fontSize: "11px", marginTop: 4, color: "#666" }}>
              {JSON.stringify(details, null, 2)}
            </pre>
          </div>
        )
      }
      type={getAlertType()}
      icon={getIcon()}
      closable={!!onClose}
      onClose={onClose}
      className={className}
      showIcon
      style={{ marginBottom: 16 }}
    />
  );
};

export default ErrorNotification;
